import { useQueryClient } from "@tanstack/react-query";
import { ClosingTabProps } from "features/closings/tabs/ClosingTabProps";
import AddCommitmentModal from "features/commitments/AddCommitmentModal";
import { CommitmentBulkAddModal } from "features/commitments/CommitmentBulkAddModal";
import { CommitmentsTable } from "features/commitments/CommitmentsTable";
import {
  getClosingStatsRetrieveQueryKey,
  useClosingCommitmentAdd,
  useClosingCommitmentRemove,
  useClosingRetrieveSuspense,
} from "gen/endpoints/closing/closing";
import { getCommitmentInformationTableDataRetrieveQueryKey } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { useSecurityOwnershipDelete } from "gen/endpoints/security-ownership/security-ownership";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";
import { useBoolean } from "usehooks-ts";

export const ClosingTabCommitments: React.FC = () => {
  const { t } = useTranslation(["closing", "commitment"]);
  const { showErrorToast, showToast } = useToast();
  const queryClient = useQueryClient();

  const { closingId, fundFamilyId } = useOutletContext<ClosingTabProps>();

  const { data: closingData } = useClosingRetrieveSuspense(
    fundFamilyId,
    closingId,
  );
  const { mutateAsync: addCommitmentToClosing } = useClosingCommitmentAdd();
  const { mutateAsync: removeCommitmentFromClosing } =
    useClosingCommitmentRemove();
  const { mutateAsync: deleteSecurityOwnership } = useSecurityOwnershipDelete();

  const {
    value: isAddClosingCommitmentDialogOpen,
    setTrue: openAddClosingCommitmentDialog,
    setFalse: closeAddClosingCommitmentDialog,
  } = useBoolean();

  const openBulkAddModal = useConfirmationDialog(CommitmentBulkAddModal, {
    fundFamilyId,
    closing: closingData,
  });

  const fundFamilyEventId = closingData.fund_family_event_id;
  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      query: {
        enabled: !!fundFamilyEventId,
      },
    },
  );
  const isDraft =
    fundFamilyEventData?.state === FundFamilyEventStateMachineFieldsEnum.draft;

  const onSuccess = useCallback(
    async (message: string) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getCommitmentInformationTableDataRetrieveQueryKey({
            fund_family_id: fundFamilyId,
            closing_id: closingId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: getCommitmentInformationTableDataRetrieveQueryKey({
            fund_family_id: fundFamilyId,
            is_unattached_to_closing: true,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: getClosingStatsRetrieveQueryKey(fundFamilyId, closingId),
        }),
      ]);
      showToast({ message, variant: "success" });
    },
    [closingId, fundFamilyId, queryClient, showToast],
  );

  const onRemove = useCallback(
    async (commitmentId: string) => {
      try {
        await removeCommitmentFromClosing({
          fundFamilyId,
          closingId,
          data: { id: commitmentId },
        });
        await onSuccess(t("closing:removedCommitmentFromClosing"));
      } catch (e) {
        showErrorToast(e);
      }
    },
    [
      closingId,
      fundFamilyId,
      onSuccess,
      removeCommitmentFromClosing,
      showErrorToast,
      t,
    ],
  );

  const onDeleteCommitment = useCallback(
    async (commitmentId: string) => {
      try {
        await deleteSecurityOwnership({
          fundFamilyId,
          id: commitmentId,
        });
        await onSuccess(t("commitment:delete.success"));
      } catch (e) {
        showErrorToast(e);
      }
    },
    [fundFamilyId, onSuccess, deleteSecurityOwnership, showErrorToast, t],
  );

  const onAdd = useCallback(
    async (commitmentId: string) => {
      try {
        await addCommitmentToClosing({
          fundFamilyId,
          closingId,
          data: { id: commitmentId },
        });
        await onSuccess(t("closing:addedCommitmentToClosing"));
      } catch (e) {
        showErrorToast(e);
      }
    },
    [
      addCommitmentToClosing,
      closingId,
      fundFamilyId,
      onSuccess,
      showErrorToast,
      t,
    ],
  );

  return (
    <>
      <PageTabBody>
        <CommitmentsTable
          fundFamilyId={fundFamilyId}
          tableTitle={t("closing:includedCommitments")}
          params={{ closing_id: closingId }}
          onClickAddCommitment={openAddClosingCommitmentDialog}
          onClickRowMenuItem={isDraft ? onRemove : undefined}
          onClickDeleteCommitment={isDraft ? onDeleteCommitment : undefined}
          onClickBulkAddCommitments={openBulkAddModal}
        />
        {isDraft && (
          <QueryBoundary>
            <CommitmentsTable
              fundFamilyId={fundFamilyId}
              tableTitle={t("closing:notIncludedCommitments")}
              params={{ is_unattached_to_closing: true }}
              onClickRowMenuItem={onAdd}
              onClickDeleteCommitment={onDeleteCommitment}
            />
          </QueryBoundary>
        )}
      </PageTabBody>
      {isAddClosingCommitmentDialogOpen && (
        <AddCommitmentModal
          fundFamilyId={fundFamilyId}
          closing={closingData}
          onCloseComplete={closeAddClosingCommitmentDialog}
        />
      )}
    </>
  );
};
